import { Capacitor } from '@capacitor/core';

import { isSessionAudioHeld, onVoicePathReady } from './voicePlaybackPath';

/**
 * MAY THIS APP CREATE RENDERER AUDIO OUTPUT RIGHT NOW? — the one rule, in one
 * place, for every early opener.
 *
 * WHY THERE HAS TO BE A RULE AT ALL. Chromium gives its ONE renderer output
 * stream an `AudioAttributes.usage` exactly once, when it CREATES that stream,
 * and chooses it from its own communication-mode flag — which follows an active
 * WebRTC capture. A stream born before the microphone is `USAGE_MEDIA`, follows
 * the media strategy, and is unreachable by `setCommunicationDevice()` for its
 * whole life. The full measurement is in `utils/voicePlaybackPath.ts`; this
 * module is the *permission* half of that same fact.
 *
 * THE CONSEQUENCE, MEASURED ON DAVID'S OWN FAILING SESSION (SM-G981U1, Android
 * 13, 2026-09-17 08:39, out of the logcat ring buffer). Two AAudio OUTPUT
 * streams were born `usage=1` (MEDIA) at 08:39:22.62 and .64 — 94 ms BEFORE the
 * cue module was even fetched, and 733 ms before `setMode(MODE_IN_COMMUNICATION)`:
 *
 *   08:39:22.624  BUILD OUTPUT usage=1        <- the renderer's output stream
 *   08:39:22.718  FETCH circle-sounds.service
 *   08:39:23.357  SETMODE 3
 *   08:39:24.998  BUILD INPUT  usage=2        (the microphone)
 *
 * and the app's own diagnostic then read, for the entire circle:
 *
 *   spk=true vcvol=8/8 mode=3 route=loudspeaker usages=["media","media","media"]
 *
 * Every cue played on `STREAM_MUSIC` — index 1 of 15 on that handset — while
 * `MODE_IN_COMMUNICATION` points the volume keys at `STREAM_VOICE_CALL`. So the
 * cues were not "quiet", they were on a stream the person had no control over:
 * David drove the call volume to 8/8 and heard nothing. The 1:1 is fine on the
 * same build because its stream is born VOICE behind `holdSessionAudio()`.
 *
 * THE REMAINING EARLY OPENER, by elimination, was `audio.service`'s
 * `setupAutoUnlock()` → `unlock()`: a document-capture `click/touchstart/
 * touchend/keydown` listener, registered at module construction, that builds an
 * `AudioContext`, `resume()`s it and plays a silent element. The tap it fires on
 * is, by design, the very tap that navigates into a circle. Commit 5144da38
 * removed the equivalent eager unlock from `circle-sounds.service` and left this
 * sibling live — CLAUDE.md class D, the fix that got one of N call sites.
 *
 * WHY A MODULE AND NOT A FLAG IN EACH OPENER. There are four places in
 * `app/src` that can construct an `AudioContext` and two that can play a silent
 * element, owned by four different features. One output stream, many owners, is
 * exactly the shape where a gate passed between them is a gate one of them
 * forgets. `utils/__tests__/audio-output-birth-sites.test.ts` fails on a new
 * construction site that neither consults this module nor is allow-listed with
 * a reason, so the sweep cannot silently rot.
 */

/**
 * WHEN is the opening being asked about? The same rule gives two different
 * answers, and conflating them is how the circle's cue context got re-broken
 * once already.
 *
 *   'now'                    a ONE-SHOT open, at this instant. Legal on native
 *                            Android only while the in-call session is up and
 *                            the microphone is open — then the birth is
 *                            `USAGE_VOICE_COMMUNICATION` and costs nothing.
 *
 *   'on-any-future-gesture'  a STANDING document listener that will open output
 *                            on some unknown later tap. Never legal on native
 *                            Android: the listener cannot know whether the next
 *                            tap is a dashboard tap or the tap that enters a
 *                            circle, and that unknowable tap IS the defect
 *                            above. This is the answer 5144da38 gave the cue
 *                            context, restated as the rule rather than as a
 *                            local platform check.
 */
export type OutputOpenMoment = 'now' | 'on-any-future-gesture';

/**
 * ANDROID IS THE ONLY PLATFORM THIS RULE RESTRICTS, and that asymmetry is
 * deliberate rather than an oversight.
 *
 * On iOS/WebKit an `AudioContext` created outside a user gesture stays
 * suspended and `resume()` can be refused outright, so the eager unlock is the
 * only path there is (Codex #718 r6 — two of the cue tests hold that line). On
 * the web the desktop autoplay policy wants the same thing.
 *
 * Android has neither rule. Capacitor's own WebView sets
 * `setMediaPlaybackRequiresUserGesture(false)` (`@capacitor/android`
 * `Bridge.java:586`), so nothing on Android needs a gesture to play at all, and
 * shipping Android ALREADY depends on lazy opening: `suspendCircleCueContext()`
 * parks the cue context at the end of every circle and only the lazy resume in
 * `audioContext()` brings it back for the next one. So refusing the eager open
 * here removes a cost and buys back a routable session; it adds no dependency
 * that is not already in production.
 *
 * `getPlatform()` rather than `isNativePlatform()`: a browser on an Android
 * handset answers `'web'`, and it is the native WebView's renderer-stream
 * typing — not the hardware — that this rule is about.
 */
function isNativeAndroid(): boolean {
  return Capacitor.getPlatform() === 'android';
}

/**
 * Is the platform's in-call audio session ESTABLISHED — `setMode(
 * MODE_IN_COMMUNICATION)` on Android, an active `AVAudioSession` on iOS?
 *
 * ONE COPY of this fact (class E), written from the single place both surfaces
 * already funnel through: `audioRouting.setSpeakerphoneOn()`, which the circle
 * calls from `CircleSession`'s session effect and the 1:1 from
 * `registerLiveCall()`. Set after the native call RESOLVES, so it means "the
 * mode has been set", not "the mode has been asked for".
 */
let inCallSession = false;

/** One-shot opens waiting for a session they can safely be born into. */
let waiting: Array<() => void> = [];

/** The in-call session is up and the mode is set. */
export function noteInCallSessionEstablished(): void {
  inCallSession = true;
  flushWaiting();
}

/** The in-call session has been handed back; the mode is gone. */
export function noteInCallSessionReleased(): void {
  inCallSession = false;
  // Anything still waiting STAYS waiting, armed for the next session. It is a
  // silent unlock: running it late is free, and dropping it would quietly
  // un-arm a retry that costs nothing.
}

/** Exposed for the diagnostics and the tests. */
export function isInCallSessionEstablished(): boolean {
  return inCallSession;
}

/**
 * The rule. `true` means a stream created right now will be typed the way this
 * app needs it typed.
 */
export function mayOpenAudioOutput(moment: OutputOpenMoment = 'now'): boolean {
  if (!isNativeAndroid()) return true;
  if (moment === 'on-any-future-gesture') return false;
  // THE MODE IS NOT ENOUGH. Chromium's flag follows the CAPTURE, not
  // AudioManager's mode, and on the reference handset the microphone landed
  // 1.6 s after `setMode(3)`. A one-shot open in that window is a coin flip, so
  // the voice-path hold — which both `JanusAudioRoom` and `PeerAudioRoom` take
  // in a layout effect before the session is acquired — is part of the answer.
  return inCallSession && !isSessionAudioHeld();
}

/**
 * Open now, or as soon as opening is safe.
 *
 * Never on a timer, and never on the mode alone: a deferred open is handed to
 * `onVoicePathReady`, so it lands after the microphone rather than merely after
 * `setMode`. On iOS/web it runs immediately, which is the behaviour those
 * platforms have always had.
 *
 * At most one open is queued per caller; a caller that arms twice queues twice,
 * so callers arm once (see `audio.service`'s `autoUnlockArmed`).
 */
export function whenAudioOutputMayOpen(open: () => void): void {
  if (mayOpenAudioOutput('now')) {
    open();
    return;
  }
  waiting.push(open);
}

function flushWaiting(): void {
  if (waiting.length === 0) return;
  const pending = waiting;
  waiting = [];
  for (const open of pending) {
    // The hold is released when the microphone is open (or its deadline
    // passed), which is the moment a birth here is communication-typed.
    onVoicePathReady(() => {
      try {
        open();
      } catch {
        /* one opener's failure is not the next one's problem */
      }
    });
  }
}

/** Test seam: forget the session and drop anything armed against it. */
export function resetAudioOutputBirth(): void {
  inCallSession = false;
  waiting = [];
}
